// Record money — DELIVERY CHARGE (2 Oct 2026).
//
// Operator: "Delivery Charges we take in from customers, sometimes in USDT,
// sometimes in cash AED - Prepare a profit account for that and mechanism to
// record those things, we charge that for MC delivery as well. rather than
// complicating current build, do u suggest a seperate mechanism?" — yes: this
// entry. A delivery charge is flat, sometimes absent and does not follow from
// a trade's numbers, so it is NOT a field in the trade or cheque forms; the
// trade or cheque it belongs to is named on it (?deal=…) and never changed by
// it. It lands on its own profit line, "Delivery charges".
//
// WHAT THIS FORM POSTS (bff routes/charges.ts, lib/charge.ts):
//
//   taken in money ......................... ONE entry: the charge IN THAT
//     MONEY, and — when it was paid now — the money into the account it was
//     paid into. Money is any of the four codes the books carry (AED, INR,
//     USD, EUR — bff lib/charge.ts CHARGE_MONEY; on this desk it is AED in
//     practice). A USD charge is USD on "Delivery charges", as an INR margin
//     is INR on "Spread": the Profit screen keeps each currency apart. Four,
//     not one, on purpose (947ff83): every cost book is a coin priced in one
//     of these, so money income has no stock to sit outside of.
//   taken in a coin, left on their account .. ONE entry: the charge in AED at
//     the rate typed, with the coin and the rate stamped on it.
//   taken in a coin, paid now ............... THREE entries, in this order,
//     because a coin is never income here — it is valued in AED and the cost
//     book takes coins in through trades only:
//       1. the coins received into the wallet (Record money's own receipt),
//       2. the coins converted on their account to AED at the rate (the
//          Convert-balance door — the desk BUYS them at that rate),
//       3. the charge, in AED, which that conversion pays.
//     Each is an entry the desk already has; nothing new is taught to the
//     trade or transfer routes. After the three the party's box is where it
//     started and the wallet holds the coins.
//
// THREE KEYS, EACH HELD ACROSS A RETRY (the sub-broker screen's rule): the two
// references and the deal id are minted once and kept in refs; `landedRef`
// records what the books have answered, so a retry posts only what has not
// landed. Once anything has landed the figures lock to it.
//
// This file owns the form only. RecordMoney owns the mode selector and the
// page header and renders this body, the way it renders the other modes.
// Nothing posts without the operator's tap.

import { useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, refreshMoneyViews } from '../lib/api';
import { tg } from '../lib/telegram';
import { ASSETS, isFiat, kindsForCategory, makeReference, partyPath, treasuryPath } from '../lib/workflow';
import { amountOk, amountProblem } from '../lib/amount';
import { coinValueText, chargeRateProblem, chargePlacesProblem, COIN_PLACES, MONEY_PLACES } from '../lib/charge-value';
import { errorWords, isSentinel, nothingLanded, readWords } from '../lib/errors';
import { Combobox, type ComboboxOption } from '../components/Combobox';
import { rosterCut, usePartySearch } from '../lib/roster-cut';
import { AssetPicker } from '../components/AssetPicker';
import { RailPicker, type RailValue } from '../components/RailPicker';
import { AsOfChips } from '../components/AsOfChips';
import { Field, FieldRow } from '../components/Field';
import { ErrorLine } from '../components/ErrorLine';
import { AccountsDown, StateLine } from '../components/kit';
import { CopyRef } from '../components/CopyRef';
import { MoreDetails } from '../components/MoreDetails';
import { RepeatPanel, useRepeatGuard } from '../components/RepeatPanel';
import {
  partyName, partySubtitle, isStale, needsNameReview, rankForQuery, partyBoxWords, sameAsset, railName,
} from '../lib/naming';
import { displayAsset, fmtAmount, fmtDay, dubaiDate, dubaiWallToIso, backdateProblem } from '../lib/format';
import { readState } from '../lib/read-state';
import { uiV2 } from '../lib/ui-v2';

const ENTITY = 'dubaiotc' as const;
/** The desk's money on this desk: what a charge taken in coin is counted in. */
const MONEY = 'AED';

/** A figure and its unit as ONE value (a no-break space joins them; design system §5): "146.40 AED". */
const money = (a: string | number, code: string, places?: number) => `${fmtAmount(a, code, places)}\u00a0${displayAsset(code)}`;

/** What the last posting said — read from what was POSTED, never from the live fields. */
export interface Posted {
  name: string; charge: string; chargeAsset: string; coin: { amount: string; asset: string; rate: string } | null;
  /** The account's name as the picker shows it, or null for "on their account". */
  paidInto: string | null; asOf: string | null; reference: string; deduped: boolean; deal: string | null;
}

/** The answer to a charge: a headline (design system §3: one line, at most ten words — the amount and its unit one
 *  value) and, under it in muted type, who and where. The coins and the rate it was taken at are behind Details.
 *  A deduped answer says so instead. */
export function PostedLine({ posted }: { posted: Posted }) {
  if (posted.deduped) {
    return (
      <StateLine tone="warn" action={<Link to="/movements" className="btn-link text-meta">Money movements →</Link>}>
        Already recorded as <span className="font-mono">{posted.reference}</span> — nothing changed; check its date.
      </StateLine>
    );
  }
  return (
    <div className="space-y-0.5">
      <StateLine
        tone="good"
        action={<CopyRef value={posted.reference} />}
        more={posted.coin ? `Taken in ${money(posted.coin.amount, posted.coin.asset)} at ${posted.coin.rate}.` : undefined}
      >
        ✓ Charged <span className="whitespace-nowrap">{money(posted.charge, posted.chargeAsset, 2)}</span>
        {posted.asOf ? ` as of ${fmtDay(posted.asOf)}` : ''}
      </StateLine>
      <p className="text-meta text-tg-hint">{posted.name} · {posted.paidInto ? `into ${posted.paidInto}` : 'on their account'}</p>
    </div>
  );
}

type Step = 'receipt' | 'convert' | 'charge';
/** What has landed, by step — the record a retry reads before it posts. */
type Landed = Partial<Record<Step, { reference: string; deduped: boolean }>>;

export function RecordCharge() {
  const [search] = useSearchParams();
  // A link from a trade's panel, a cheque's page or a pending Chain row lands
  // here with its facts: the party, the deal it belongs to, the wallet and the
  // coin the money arrived in, the amount, and the chain note that lets the
  // matcher tie the receipt to that row by itself.
  const [slug, setSlug]       = useState(search.get('party') ?? '');
  const linkedDeal            = (search.get('deal') ?? '').trim();
  const chainNote             = (search.get('note') ?? '').startsWith('chain ') ? (search.get('note') as string) : '';
  const urlAsset              = search.get('railAsset') ?? search.get('asset') ?? '';
  // Is the link's code one the picker offers? Membership in the registry
  // list itself — the picker speaks these exact codes — not a comparison
  // between two assets, which is what the dialect gate is about.
  const offered = (ASSETS as readonly string[]).includes(urlAsset);   // dialect-ok: membership in the registry list, not an asset comparison
  const [asset, setAsset]     = useState<string>(offered ? urlAsset : MONEY);
  const [amount, setAmount]   = useState(search.get('amount') ?? '');
  const [rate, setRate]       = useState(search.get('rate') ?? '');
  const [paidNow, setPaidNow] = useState(search.get('paid') !== 'account');
  const [rail, setRail]       = useState<RailValue>({ slug: search.get('rail') ?? '', asset: search.get('rail') ? urlAsset : '' });
  const [notes, setNotes]     = useState('');
  const [when, setWhen]       = useState(search.get('when') ?? '');
  const whenIso   = when ? dubaiWallToIso(when) : '';
  const whenWords = backdateProblem(when);
  // Did the whole transfer on the Chain row come for this charge? Only then
  // does the receipt carry the row's note (see the receipt below).
  const sentAmount = search.get('amount') ?? '';
  const wholeSend  = !!chainNote && sentAmount !== '' && Number(amount) === Number(sentAmount);

  // ── the party ────────────────────────────────────────────────────────────
  const parties = usePartySearch({
    base: ['parties', ENTITY], text: slug,
    ask: (q) => api.listParties({ entity: ENTITY, q, with_balances: true }),
    enabled: slug.length > 0,
  });
  const rows = parties.data?.parties ?? [];
  // WHAT THE LIST IS WORTH — `readState` (lib/read-state.ts; Appendix B): the names, and the balances (a list the BFF flags
  // `balances_read: false` has the names and not the balances: its empty balances are not "square"). The picker's box words take the
  // balances' answer.
  const lookup = readState(parties);
  const listBalances = readState(parties, { balances: true });
  const listBalancesRead = listBalances === 'known';
  // That unread list says so once, with a Retry, whatever is typed (one query per search; the flag is read off the list showing).
  const listBalancesFlagged = lookup === 'known' && listBalances === 'failed';
  const partyOptions: ComboboxOption[] = useMemo(() =>
    // OWN ACCOUNTS ARE NOT PARTIES HERE: a charge is raised on a customer or a
    // merchant; a desk account pays the desk nothing.
    rankForQuery(rows.filter((p) => p.party_type !== 'own_account'), slug).map((p) => ({
      value:     p.slug,
      label:     partyName(p),
      secondary: [partySubtitle(p, { compact: true }), listBalancesRead ? partyBoxWords(p) : ''].filter(Boolean).join(' · '),
      stale:     isStale(p),
      review:    needsNameReview(p.display_name),
    }))
  , [parties.data, listBalancesRead, slug]);   // eslint-disable-line react-hooks/exhaustive-deps
  const selected = rows.find((p) => p.slug === slug && p.party_type !== 'own_account') ?? null;
  const name = selected ? partyName(selected) : slug;

  // ── the accounts the money can be paid into ──────────────────────────────
  const treasuryRows = useQuery({
    queryKey: ['treasury', 'balances', ENTITY],
    queryFn:  () => api.treasuryBalances(ENTITY),
  });
  // What the account list is worth: 'known' only once a read SUCCEEDED.
  const accounts = readState(treasuryRows);

  // The account's name as the picker shows it — the slug alone reads "Hot · TRC" beside the picker's "Hot wallet · TRC".
  const railLabel = railName((treasuryRows.data?.rows ?? []).find((r) => r.slug === rail.slug && sameAsset(r.asset, rail.asset)) ?? { slug: rail.slug });

  // ── the figures ──────────────────────────────────────────────────────────
  const coin        = !isFiat(asset);
  // The figures are held to the Worker's own rules HERE, at the box: in the
  // three-entry case the charge is asked last, and a refusal there would come
  // with the receipt and the conversion already posted (lib/charge-value.ts).
  const amountWords = amountProblem(amount) ?? chargePlacesProblem(amount, coin ? COIN_PLACES : MONEY_PLACES);
  const rateWords   = coin ? chargeRateProblem(rate) : null;
  // What the charge comes to in the desk's money: the amount itself when it
  // was taken in money, the coins at the rate when it was taken in coin —
  // worked in integers, half-even, the figure the Worker will hold it to.
  const chargeText  = coin ? (amountOk(amount) ? coinValueText(amount.trim(), rate) : null) : (amountOk(amount) ? amount.trim() : null);
  const chargeAsset = coin ? MONEY : asset;
  const chargeN     = chargeText ? Number(chargeText) : 0;
  // The account must hold what was taken: the picker hands back the rail's
  // own asset, and a wallet that carries another coin is the wrong wallet.
  const railProblem = paidNow && rail.slug && rail.asset && !sameAsset(rail.asset, asset)
    ? `That account holds ${displayAsset(rail.asset)}, not ${displayAsset(asset)} — pick another.`
    : null;

  // ── submit ───────────────────────────────────────────────────────────────
  const chgRef    = useRef<string | null>(null);
  const rcptRef   = useRef<string | null>(null);
  const dealIdRef = useRef<string | null>(null);
  const landedRef = useRef<Landed>({});
  const [landed, setLanded] = useState<Landed>({});
  const { twin, clearTwin, guard } = useRepeatGuard();
  const askingRef    = useRef<Step>('charge');
  // WHICH guard refused, by step (27 Oct 2026): the BFF takes `confirm_repeat` for a twin and `confirm_money_moved_again` for a settled
  // trade that already moved this money — one yes answers one question (routes/transfers.ts). The coins-in step is a transfer with a
  // party at one end, so it can meet either; each yes is kept as `${step}:${kind}` and goes out as the flag that answers the refusal it was
  // given to. A yes that always went out as confirm_repeat left a money twin on the receipt refused for ever behind its own panel.
  const confirmedRef = useRef<Set<string>>(new Set());

  const [posted, setPosted] = useState<Posted | null>(null);

  const post = async () => {
    if (!selected || !chargeText) throw new Error('nothing to record');
    const L = landedRef.current;
    const three = coin && paidNow;
    const coinFacts = coin ? { asset, amount: Number(amount), rate: rate.trim() } : undefined;
    const tail = [notes.trim(), linkedDeal ? `for ${linkedDeal}` : ''].filter(Boolean).join(' · ');

    // (1) THE COINS, in: party → the wallet. Record money's own receipt, with
    // the chain note FIRST when this came from a Chain row — the matcher ties
    // a booking that names its send by itself.
    if (three && !L.receipt) {
      rcptRef.current ??= makeReference('RCPT', whenIso || undefined);
      const reference = rcptRef.current;
      askingRef.current = 'receipt';
      const data = await guard(() => api.transfer({
        source:      partyPath(selected.slug, selected.party_type, ENTITY),
        destination: treasuryPath(rail.slug, asset, ENTITY),
        amount:      { asset, amount: Number(amount) },
        fee_payer:   'house',
        reference,
        // The chain note names a send as THIS booking (the matcher ties by it,
        // and two bookings naming one send read as a double entry). So it rides
        // only when the whole transfer is the charge; a charge that is part of
        // a larger transfer — 10,020 for a 10,000 trade — is tied on the Chain
        // screen to both entries instead (Tie all 2).
        notes:       [wholeSend ? chainNote : '', `delivery charge, taken in ${displayAsset(asset)}`, tail].filter(Boolean).join(' · '),
        ...(linkedDeal ? { linked_deal_id: linkedDeal } : {}),
        effective_at:   whenIso || undefined,
        confirm_repeat: confirmedRef.current.has('receipt:repeat') || undefined,
        confirm_money_moved_again: confirmedRef.current.has('receipt:money') || undefined,
      }));
      L.receipt = { reference: data.reference ?? reference, deduped: !!data.deduped };
      setLanded({ ...L });
    }

    // (2) THE COINS BECOME AED ON THEIR ACCOUNT, at the rate: the desk buys
    // them — a trade, so the cost book takes them in at exactly this rate.
    if (three && !L.convert) {
      dealIdRef.current ??= `DEAL-${whenIso ? dubaiDate(whenIso) : dubaiDate()}-${Math.random().toString(36).slice(2, 7).toUpperCase()}`;
      const dealId = dealIdRef.current;
      askingRef.current = 'convert';
      const data = await guard(() => api.bookTrade({
        entity:            ENTITY,
        direction:         'customer_sell',
        party_slug:        selected.slug,
        unsettled:         true,
        settle_on_account: true,
        amount_take:       { asset, amount: Number(amount) },
        amount_give:       { asset: MONEY, amount: chargeN },
        rate:              rate.trim(),
        deal_id:           dealId,
        notes:             ['coins taken for a delivery charge', tail].filter(Boolean).join(' · '),
        effective_at:      whenIso || undefined,
        confirm_repeat:    confirmedRef.current.has('convert:repeat') || undefined,
        confirm_money_moved_again: confirmedRef.current.has('convert:money') || undefined,
        // THE OVER-BALANCE QUESTION IS ALREADY ANSWERED HERE. The trade door
        // refuses a conversion bigger than the balance it converts (409
        // over_balance — it would flip the box the other way), judging this one
        // entry on its own. In this flow it is one of three: the receipt above
        // put exactly these coins on the box and the charge below takes exactly
        // this AED off it, so across the three the party's position does not
        // move at all. A party who already owed the desk 30 AED, or 5 USDT,
        // would otherwise have step 2 refused with the receipt already posted
        // and no way forward on this screen.
        confirm_over_balance: true,
      }));
      L.convert = { reference: data.deal_id ?? dealId, deduped: !!data.deduped };
      setLanded({ ...L });
    }

    // (3) THE CHARGE. In money and paid now: with the account it was paid
    // into. In coin: in AED on their account — the conversion above (or the
    // coins when they arrive) is what pays it.
    if (!L.charge) {
      chgRef.current ??= makeReference('CHG', whenIso || undefined);
      const reference = chgRef.current;
      askingRef.current = 'charge';
      const data = await guard(() => api.recordCharge({
        entity:       ENTITY,
        party_slug:   selected.slug,
        kind:         'delivery',
        amount:       { asset: chargeAsset, amount: chargeN },
        ...(!coin && paidNow ? { paid_into_slug: rail.slug } : {}),
        ...(linkedDeal ? { linked_deal_id: linkedDeal } : {}),
        ...(coinFacts ? { coin: coinFacts } : {}),
        reference,
        notes:        notes.trim() || undefined,
        effective_at: whenIso || undefined,
        confirm_repeat: confirmedRef.current.has('charge:repeat') || undefined,
      }));
      L.charge = { reference: data.reference ?? reference, deduped: !!data.deduped };
      setLanded({ ...L });
    }

    return {
      name, charge: chargeText, chargeAsset,
      coin: coin ? { amount: amount.trim(), asset, rate: rate.trim() } : null,
      paidInto: paidNow ? railLabel : null,
      asOf: whenIso || null, reference: L.charge.reference, deduped: L.charge.deduped, deal: linkedDeal || null,
    };
  };

  const qc = useQueryClient();
  const submit = useMutation({
    mutationFn: post,
    onSuccess: (r) => {
      setPosted(r);
      // Every key leaves with the entry set it was minted for.
      landedRef.current = {}; setLanded({});
      chgRef.current = null; rcptRef.current = null; dealIdRef.current = null;
      confirmedRef.current.clear(); clearTwin();
      refreshMoneyViews(qc);
      // The figures leave with the entry; the party, the coin and the account
      // stay — the next delivery that day shares them.
      setAmount(''); setNotes(''); setWhen('');
      tg()?.HapticFeedback.notificationOccurred('success');
    },
    onError: (e) => { if (!isSentinel(e)) tg()?.HapticFeedback.notificationOccurred('error'); },
  });

  // Once an entry has landed — or may have: a failure not provably answered
  // before the first ledger write keeps its key, so the retry dedupes to the
  // figures FIRST sent — the figures lock, and only "start another" lets go.
  const mayHold = submit.isError && !nothingLanded(submit.error);
  const anyLanded = !!(landed.receipt || landed.convert || landed.charge);
  const locked  = anyLanded || mayHold;
  const busy    = submit.isPending;

  const onWhenChange = (v: string) => {
    setWhen(v);
    // The keys carry the day: a new day, new keys — unless the books may hold one.
    if (!busy && !locked) { chgRef.current = null; rcptRef.current = null; dealIdRef.current = null; confirmedRef.current.clear(); clearTwin(); }
  };
  const startAnother = () => {
    landedRef.current = {}; setLanded({});
    chgRef.current = null; rcptRef.current = null; dealIdRef.current = null;
    confirmedRef.current.clear(); clearTwin(); submit.reset();
  };

  const canPost = !busy && !!selected && amountOk(amount) && !amountWords && !!chargeText && chargeN > 0
    && (!coin || (!!rate.trim() && !rateWords))
    && (!paidNow || (!!rail.slug && !railProblem))
    && !whenWords;
  // Why the button is off, in a few words — the rebuilt console only (uiV2(3), Record's rule; Screens' audit, 27 Oct
  // 2026). Today's console draws exactly what it drew. A wrong figure, the account's asset and the date say their own words.
  const why = !uiV2(3) || busy || canPost ? null
    : !selected ? 'Pick a party.'
    : !amountOk(amount) ? 'Enter an amount.'
    : coin && !rate.trim() ? 'Enter the rate.'
    : paidNow && !rail.slug ? 'Pick an account.'
    : null;

  const remaining = coin && paidNow
    ? (landed.receipt ? 0 : 1) + (landed.convert ? 0 : 1) + (landed.charge ? 0 : 1)
    : (landed.charge ? 0 : 1);

  return (
    <div className="space-y-3 lg:space-y-4">
      {/* The deal this charge belongs to, when it came from a trade's or a
          cheque's page. Said, not editable: the link is the fact. */}
      {linkedDeal && (
        <p className="text-meta text-tg-hint">
          For <span className="font-mono text-tg-text">{linkedDeal}</span>; the deal is left as it is.
        </p>
      )}

      <FieldRow>
        <Field label="Party">
          <Combobox
            value={slug}
            // Locked once an entry has landed: the party is part of what landed.
            onChange={(v) => { if (!locked) setSlug(v); }}
            options={partyOptions}
            more={rosterCut(parties.data)}
            loading={slug.length > 0 && lookup === 'waiting'}
            placeholder="Search party"
            emptyHint={slug.length === 0 ? null : lookup === 'failed' ? 'Parties could not be read.' : undefined}
          />
        </Field>
        <Field label="Taken in">
          <AssetPicker value={asset} onChange={(a) => { if (!locked) { setAsset(a); setRail({ slug: '', asset: '' }); } }} options={ASSETS} />
        </Field>
      </FieldRow>
      {/* A READ THAT FAILED IS NOT AN EMPTY ONE — the parties, and the accounts the money is paid into. */}
      {(lookup === 'failed' || (paidNow && accounts === 'failed')) && (
        <StateLine
          tone="warn"
          more={readWords(lookup === 'failed' ? parties.error : treasuryRows.error)}
          action={<button type="button" className="btn-link text-meta" onClick={() => { if (lookup === 'failed') void parties.refetch(); if (accounts === 'failed') void treasuryRows.refetch(); }}>Retry</button>}
        >
          {`Couldn’t read ${lookup === 'failed' && paidNow && accounts === 'failed' ? 'parties and accounts' : lookup === 'failed' ? 'parties' : 'the accounts'} — try again.`}
        </StateLine>
      )}
      {listBalancesFlagged && (
        <StateLine tone="warn" action={<button type="button" className="btn-link text-meta" onClick={() => void parties.refetch()}>Retry</button>}>
          Balances not read — try again.
        </StateLine>
      )}

      <FieldRow>
        <Field label={`Charge in ${displayAsset(asset)}`}>
          <input className="input" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" disabled={locked} />
          <ErrorLine>{amountWords}</ErrorLine>
        </Field>
        {coin && (
          <Field label={`Rate — ${MONEY} for one ${displayAsset(asset)}`}>
            <input className="input" value={rate} onChange={(e) => setRate(e.target.value)} inputMode="decimal" disabled={locked} placeholder="3.66" />
            <ErrorLine>{rateWords}</ErrorLine>
          </Field>
        )}
      </FieldRow>
      {/* A coin charge is counted in AED: say the figure before the tap. */}
      {coin && chargeText && (
        <p className="text-meta max-lg:text-body text-tg-hint">
          Counted as <span className="text-tg-text tabular whitespace-nowrap">{fmtAmount(chargeText, MONEY, 2)} {MONEY}</span> of delivery charges.
        </p>
      )}

      <FieldRow>
        <Field label="Paid">
          <div className="flex gap-2">
            <button type="button" className={`chip ${paidNow ? 'chip-on' : ''}`} disabled={locked} onClick={() => setPaidNow(true)}>Paid now</button>
            <button type="button" className={`chip ${!paidNow ? 'chip-on' : ''}`} disabled={locked} onClick={() => setPaidNow(false)}>On their account</button>
          </div>
        </Field>
        {paidNow && (
          <Field label="Paid into">
            {/* No list at all because the read FAILED (a failed REFRESH keeps its rows: that picker stays and is told, readFailed): an open picker would say "No such account" and point at New party. */}
            {accounts === 'failed' && !treasuryRows.data ? <AccountsDown value={rail} /> : (
              <RailPicker
                value={rail}
                onChange={(v) => { if (!landed.receipt && !landed.charge) setRail(v); }}
                rows={treasuryRows.data?.rows ?? []}
                allowedKinds={kindsForCategory(coin ? 'crypto' : 'fiat')}
                partySlug={selected ? selected.slug : undefined}
                loading={treasuryRows.isFetching || accounts === 'waiting'}
                readFailed={accounts === 'failed'}
                placeholder="Receiving account"
              />
            )}
            <ErrorLine>{railProblem}</ErrorLine>
          </Field>
        )}
      </FieldRow>

      <FieldRow>
        <AsOfChips value={when} onChange={onWhenChange} disabled={busy || locked} />
      </FieldRow>

      <MoreDetails label="More">
        <Field label="Notes">
          <textarea className="input h-16" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything to remember" />
        </Field>
      </MoreDetails>

      {/* The read-back, once everything is filled: what will be recorded, in
          one muted line, the same shape as the entries beside it. */}
      {canPost && !anyLanded && (coin && paidNow ? (
        <StateLine
          more={`${money(amount, asset)} received into ${railLabel}; converted on ${name}'s account at ${rate.trim()}; ${money(chargeText!, MONEY, 2)} recorded as a delivery charge. ${name}'s balance ends where it started.`}
        >
          Three entries: coins in, converted at {rate.trim()}, charge recorded.
        </StateLine>
      ) : paidNow ? (
        <StateLine more={`${money(chargeText!, chargeAsset, 2)} goes into ${railLabel} and onto Delivery charges. ${name}'s balance does not change.`}>
          One entry: <span className="whitespace-nowrap">{money(chargeText!, chargeAsset, 2)}</span> onto Delivery charges.
        </StateLine>
      ) : (
        <StateLine>
          They will owe <span className="whitespace-nowrap">{money(chargeText!, chargeAsset, 2)}</span> more — onto Delivery charges.
        </StateLine>
      ))}

      {/* What stands when a set of three stopped part-way: said by entry, so
          the retry — which posts only what has not landed — can be trusted. */}
      {anyLanded && !submit.isSuccess && (
        <div className="rounded-lg bg-tg-secondary/40 p-3 text-meta space-y-1">
          {landed.receipt && <p><span className="text-pos">✓</span> Coins received as <span className="font-mono">{landed.receipt.reference}</span></p>}
          {landed.convert && <p><span className="text-pos">✓</span> Converted on their account as <span className="font-mono">{landed.convert.reference}</span></p>}
          {landed.charge && <p><span className="text-pos">✓</span> Charge recorded as <span className="font-mono">{landed.charge.reference}</span></p>}
          <StateLine
            tone="warn"
            action={<button type="button" className="btn-link text-meta" onClick={startAnother} disabled={busy}>Start another instead</button>}
          >
            {remaining > 0 && `${remaining} ${remaining === 1 ? 'entry' : 'entries'} still to record — the button retries only ${remaining === 1 ? 'that one' : 'those'}.`}
          </StateLine>
        </div>
      )}

      {twin && (
        <RepeatPanel
          twin={twin}
          pending={busy}
          // The twin IS this entry: keep it, and go on with what remains.
          onAlreadyBooked={() => {
            const step = askingRef.current;
            landedRef.current[step] = { reference: twin.reference ?? '', deduped: true };
            setLanded({ ...landedRef.current }); clearTwin();
            if (step !== 'charge') submit.mutate(); else submit.reset();
          }}
          onPostAnyway={() => { confirmedRef.current.add(`${askingRef.current}:${twin.kind}`); clearTwin(); submit.mutate(); }}
        />
      )}

      <div className={why ? 'flex flex-col-reverse gap-3 lg:flex-row lg:items-center lg:justify-between lg:border-t lg:border-stroke-soft lg:pt-5' : 'lg:flex lg:justify-end lg:border-t lg:border-stroke-soft lg:pt-5'}>
        {why && <p className="text-center text-meta text-tg-hint lg:text-left">{why}</p>}
        <button className="btn-primary w-full lg:w-auto lg:px-6" disabled={!canPost} onClick={() => submit.mutate()}>
          {busy ? 'Recording…'
            : anyLanded ? `Record the remaining ${remaining === 1 ? 'entry' : `${remaining} entries`}`
            : `Record delivery charge${whenIso && !whenWords ? ` as of ${fmtDay(whenIso)}` : ''}`}
        </button>
      </div>

      {submit.isSuccess && posted && <PostedLine posted={posted} />}
      {submit.isError && !isSentinel(submit.error) && (
        <ErrorLine className="mt-1 text-body">{errorWords(submit.error)}</ErrorLine>
      )}
    </div>
  );
}
